/**
 * Phase 5: Overnight forecast per property.
 * Aligned with fleet dashboard: reads overnight_forecasts table first (same source as useFleetSystems).
 * Calls forecast-overnight only when table has no row or forecast is stale; function writes to table.
 * Contract: docs/contracts/OVERNIGHT_FORECAST_API.md
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getFunctionsErrorBody, formatFunctionsError, isNoTelemetryError } from '@/lib/functions-error';

/** Consider table row "fresh" if forecast_end is in the future or row was created within this many ms. */
const TABLE_FRESH_MS = 12 * 60 * 60 * 1000; // 12 hours (align with fleet + forecast-orchestrator)

export interface OvernightForecastResult {
  propertyId: string;
  forecast_start: string;
  forecast_end: string;
  expected_kwh: number;
  lower_bound_kwh: number;
  upper_bound_kwh: number;
  confidence: 'low' | 'medium' | 'high';
  /** WT-337: 'empirical' (realized-residual tier, trustworthy) | 'coverage' (legacy heuristic) | null (pre-WT-337). */
  confidenceBasis: string | null;
  /** WT-337: true only when the tier came from proven residual history (else "establishing baseline"). */
  confidenceProven: boolean;
  learning_state_at_time: Record<string, unknown>;
}

function mapRowToResult(row: {
  property_id: string;
  forecast_start: string;
  forecast_end: string;
  expected_kwh: number;
  lower_bound_kwh?: number;
  upper_bound_kwh?: number;
  confidence: string;
  confidence_basis?: string | null;
  confidence_proven?: boolean;
  learning_state_at_time?: Record<string, unknown> | null;
}, propertyId: string): OvernightForecastResult {
  return {
    propertyId: row.property_id ?? propertyId,
    forecast_start: row.forecast_start ?? '',
    forecast_end: row.forecast_end ?? '',
    expected_kwh: Number(row.expected_kwh ?? 0),
    lower_bound_kwh: Number(row.lower_bound_kwh ?? row.expected_kwh ?? 0),
    upper_bound_kwh: Number(row.upper_bound_kwh ?? row.expected_kwh ?? 0),
    confidence: (row.confidence === 'high' || row.confidence === 'medium' || row.confidence === 'low') ? row.confidence : 'medium',
    confidenceBasis: row.confidence_basis ?? null,
    confidenceProven: row.confidence_proven ?? false,
    learning_state_at_time: (row.learning_state_at_time as Record<string, unknown>) ?? {},
  };
}

export function useOvernightForecast(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['overnight-forecast', propertyId],
    queryFn: async (): Promise<OvernightForecastResult | null> => {
      if (!propertyId) return null;

      // Same source as fleet: read overnight_forecasts first so fleet and property show same "~at sunrise"
      const { data: tableRow, error: tableError } = await supabase
        .from('overnight_forecasts')
        .select('property_id, forecast_start, forecast_end, expected_kwh, lower_bound_kwh, upper_bound_kwh, confidence, confidence_basis, confidence_proven, learning_state_at_time, created_at')
        .eq('property_id', propertyId)
        .order('forecast_start', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!tableError && tableRow) {
        const now = Date.now();
        const forecastEnd = new Date(tableRow.forecast_end ?? 0).getTime();
        const createdAt = tableRow.created_at ? new Date(tableRow.created_at).getTime() : 0;
        const endStillRelevant = forecastEnd >= now - TABLE_FRESH_MS;
        const rowRecent = createdAt >= now - TABLE_FRESH_MS;
        if (endStillRelevant || rowRecent) {
          return mapRowToResult(tableRow, propertyId);
        }
      }

      // No fresh row: call forecast-overnight (writes to overnight_forecasts)
      const t0 = performance.now();
      const { data, error } = await supabase.functions.invoke<{
        success: boolean;
        propertyId?: string;
        forecast_start?: string;
        forecast_end?: string;
        expected_kwh?: number;
        lower_bound_kwh?: number;
        upper_bound_kwh?: number;
        confidence?: 'low' | 'medium' | 'high';
        confidence_basis?: string | null;
        confidence_proven?: boolean;
        learning_state_at_time?: Record<string, unknown>;
      }>('forecast-overnight', { method: 'POST', body: { propertyId } });
      const duration = Math.round(performance.now() - t0);
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[perf] forecast-overnight: ${duration}ms`);
      }
      if (error) {
        const backend = await getFunctionsErrorBody(error);
        if (backend && isNoTelemetryError(backend)) {
          if (typeof console !== 'undefined' && console.log) {
            console.log('[forecast-overnight] No plant telemetry available for overnight forecast');
          }
          return null;
        }
        if (backend) console.warn('[forecast-overnight]', formatFunctionsError(backend));
        throw error;
      }
      if (data?.success && data?.expected_kwh != null) {
        return {
          propertyId: data.propertyId ?? propertyId,
          forecast_start: data.forecast_start ?? '',
          forecast_end: data.forecast_end ?? '',
          expected_kwh: data.expected_kwh,
          lower_bound_kwh: data.lower_bound_kwh ?? data.expected_kwh,
          upper_bound_kwh: data.upper_bound_kwh ?? data.expected_kwh,
          confidence: data.confidence ?? 'medium',
          confidenceBasis: data.confidence_basis ?? null,
          confidenceProven: data.confidence_proven ?? false,
          learning_state_at_time: data.learning_state_at_time ?? {},
        };
      }
      return null;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });
}
